import { ExternalLink } from "@octopusdeploy/design-system-components";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import KubernetesCategoryDefinition from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesCategoryDefinition";
import type { EndpointRegistration, CategorizedEndpointRegistration, CategoryDefinition, StepPackageDeploymentTargetRegistration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import endpointRegistry, { EndpointSelectionScope } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import DataLoader from "~/components/DataLoader";
import { PageContent } from "~/components/PageContent/PageContent";
import { PageLoading } from "~/components/PageContent/PageLoading";
import { useSpaceAwareNavigation } from "../../../../components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { EndpointSelector } from "./Endpoints/EndpointSelector";
const NewDeploymentTargetPageDataLoader = DataLoader<LoadedData>();
type LoadedData = {
    registrations: EndpointRegistration[];
};
interface NewDeploymentTargetPageProps {
    environmentId: string | undefined;
    spaceId: string;
}
export function SelectDeploymentTargetTypePage(props: NewDeploymentTargetPageProps) {
    return (<NewDeploymentTargetPageDataLoader load={async () => {
            const registrations = await endpointRegistry.getAllRegistrations();
            return { registrations };
        }} renderAlternate={({ errors }) => <PageLoading errors={errors} loadingTitle="Add Deployment Target"/>} renderWhenLoaded={(loadedData) => <NewDeploymentTargetWhenLoaded registrations={loadedData.registrations} environmentId={props.environmentId} spaceId={props.spaceId}/>}/>);
}
interface NewDeploymentTargetWhenLoadedProps {
    registrations: EndpointRegistration[];
    environmentId: string | undefined;
    spaceId: string;
}
function NewDeploymentTargetWhenLoaded({ registrations, environmentId, spaceId }: NewDeploymentTargetWhenLoadedProps) {
    const navigate = useNavigateToDeploymentTarget(spaceId, environmentId);
    const categories = endpointRegistry.categorizeEndpoints(registrations);
    return (<PageContent header={{ title: "Add Deployment Target", breadcrumbs: [{ label: "Deployment Targets", pageUrl: links.deploymentTargetsPage.generateUrl({ spaceId }) }] }}>
            <EndpointSelector spaceId={spaceId} heading={<Heading />} onSelect={navigate} scope={EndpointSelectionScope.DeploymentTarget} registrations={registrations} categories={categories} defaultCategory={KubernetesCategoryDefinition.category} showUpsellExperience={true}/>
        </PageContent>);
}
function Heading() {
    return (<>
            What type of <strong>Deployment Target</strong> do you want to set up? Learn more about <ExternalLink href="DeploymentTargets">Deployment Targets</ExternalLink>
        </>);
}
function isStepPackageDeploymentTarget(obj: EndpointRegistration): obj is StepPackageDeploymentTargetRegistration {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (obj as StepPackageDeploymentTargetRegistration).targetType !== undefined;
}
export function useNavigateToDeploymentTarget(spaceId: string, environmentId: string | undefined) {
    const navigation = useSpaceAwareNavigation();
    return React.useCallback((registration: CategorizedEndpointRegistration, category: CategoryDefinition) => {
        if (endpointRegistry.isMachineRegistration(registration) && registration.discoverable) {
            return navigation.navigate(links.discoverDeploymentTargetPage.generateUrl({ spaceId, endpointKey: registration.key }, { category: category.category, environmentId }));
        }
        else {
            if (isStepPackageDeploymentTarget(registration))
                return navigation.navigate(links.addDeploymentTargetPage.generateUrl({ spaceId }, { type: registration.communicationStyle, deploymentTargetTypeId: registration.key, environment: environmentId }));
            else
                return navigation.navigate(links.addDeploymentTargetPage.generateUrl({ spaceId }, { type: registration.communicationStyle, environment: environmentId }));
        }
    }, [environmentId, navigation, spaceId]);
}
